import React, { useState } from 'react';
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { Shield, Database, Eye, Trash2, Download, Loader } from 'lucide-react';
import { ABLE_COLORS } from '../constants';
import { recordConsent } from '../utils/memoryApi';

/**
 * ConsentDialog - First-use popup explaining data storage
 * Shows once when user first authenticates, records consent via API
 */
export default function ConsentDialog({ isOpen, onAccept }) {
  const { getToken } = useKindeAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAccept = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = await getToken();
      await recordConsent(token);
      
      // Mark consent as given in localStorage
      localStorage.setItem('mabel-consent-given', 'true');
      
      onAccept();
    } catch (err) {
      console.error('Failed to record consent:', err);
      setError('Failed to save. Please try again.');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.6)',
        zIndex: 3000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px'
      }}
    >
      <div 
        style={{
          background: 'white',
          borderRadius: '24px',
          padding: '32px',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${ABLE_COLORS.primary} 0%, ${ABLE_COLORS.secondary} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <Shield size={32} color="white" />
          </div>
          
          <h2 style={{ 
            margin: '0 0 8px 0',
            fontSize: '24px',
            color: '#333'
          }}>
            Welcome to Mabel
          </h2>
          
          <p style={{ 
            margin: 0,
            fontSize: '14px',
            color: '#666'
          }}>
            Your AI assistant that remembers
          </p>
        </div>

        {/* Main content */}
        <div style={{ marginBottom: '24px' }}>
          <p style={{ 
            fontSize: '14px', 
            color: '#333', 
            lineHeight: '1.6',
            marginBottom: '20px'
          }}>
            Mabel stores information you share to provide personalized help. 
            This data is sent to Anthropic (Claude AI) to understand your context 
            and give you better assistance.
          </p>

          {/* What we store */}
          <div style={{ 
            background: ABLE_COLORS.bg, 
            borderRadius: '12px', 
            padding: '16px',
            marginBottom: '16px'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              marginBottom: '12px'
            }}>
              <Database size={18} color={ABLE_COLORS.primary} />
              <span style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>
                What we store:
              </span>
            </div>
            <ul style={{ 
              margin: 0, 
              paddingLeft: '24px',
              fontSize: '13px',
              color: '#555',
              lineHeight: '1.8'
            }}>
              <li>Tasks and projects you mention</li>
              <li>Your capacity patterns (energy, focus)</li>
              <li>Context you share in conversation</li>
            </ul>
          </div>

          {/* Your rights */}
          <div style={{
            background: ABLE_COLORS.successBg,
            borderRadius: ABLE_COLORS.buttonRadius,
            padding: '16px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px'
            }}>
              <Eye size={18} color={ABLE_COLORS.semantic.success} />
              <span style={{ fontWeight: '600', fontSize: '14px', color: ABLE_COLORS.textPrimary }}>
                Your rights:
              </span>
            </div>
            <div 
              className="consent-rights-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '8px',
                fontSize: '13px',
                color: ABLE_COLORS.textSecondary
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Eye size={14} color={ABLE_COLORS.semantic.success} />
                <span>View all stored data</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Trash2 size={14} color={ABLE_COLORS.semantic.success} />
                <span>Delete any time</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Download size={14} color={ABLE_COLORS.semantic.success} />
                <span>Export as JSON</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Shield size={14} color={ABLE_COLORS.semantic.success} />
                <span>Control privacy level</span>
              </div>
            </div>
            <style>{`
              @media (max-width: 380px) {
                .consent-rights-grid { grid-template-columns: 1fr !important; }
              }
            `}</style>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div style={{
            padding: '12px',
            background: ABLE_COLORS.errorBg,
            borderRadius: '8px',
            color: ABLE_COLORS.semantic.error,
            marginBottom: '16px',
            fontSize: '13px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button
            onClick={handleAccept}
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: loading 
                ? '#ccc' 
                : `linear-gradient(135deg, ${ABLE_COLORS.primary} 0%, ${ABLE_COLORS.secondary} 100%)`,
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: loading ? 'wait' : 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: loading ? 'none' : `0 4px 12px ${ABLE_COLORS.primary}40`,
              transition: 'all 0.2s'
            }}
          >
            {loading ? (
              <>
                <Loader size={18} className="spin" />
                Saving...
              </>
            ) : (
              'I Understand'
            )}
          </button>
          
          <p style={{ 
            margin: 0,
            fontSize: '12px',
            color: '#888',
            textAlign: 'center'
          }}>
            You can manage your data anytime from the user menu
          </p>
        </div>
      </div>

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
